import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, PencilSimple, Trash, Plus, Note, Briefcase } from "@phosphor-icons/react";
import { StatusBadge } from "./Dashboard";
import { TierBadge } from "./LenderDirectory";
import { toast } from "sonner";

const Field = ({ label, value, mono }) => (
  <div className="border-b border-slate-100 py-2.5 grid grid-cols-3 gap-3">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className={`col-span-2 text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-400">—</span>}</div>
  </div>
);
const moneyR = (a, b) =>
  a == null && b == null ? "—" :
  `${a != null ? `$${Number(a).toLocaleString()}` : "—"} – ${b != null ? `$${Number(b).toLocaleString()}` : "—"}`;

export default function LenderProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const canManage = isAdmin || user?.role === "leader";
  const [lender, setLender] = useState(null);
  const [notes, setNotes] = useState([]);
  const [deals, setDeals] = useState([]);
  const [newNote, setNewNote] = useState("");

  // example deal form
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealForm, setDealForm] = useState({});

  const reload = async () => {
    const [{ data: l }, { data: n }, { data: d }] = await Promise.all([
      api.get(`/lenders/${id}`),
      api.get(`/lenders/${id}/notes`),
      api.get(`/lenders/${id}/example-deals`),
    ]);
    setLender(l); setNotes(n || []); setDeals(d || []);
  };
  useEffect(() => { reload().catch(() => toast.error("Failed to load lender")); /* eslint-disable-next-line */ }, [id]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    await api.post(`/lenders/${id}/notes`, { text: newNote.trim() });
    setNewNote("");
    toast.success("Note added");
    reload();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${lender.name}? This cannot be undone.`)) return;
    await api.delete(`/lenders/${id}`);
    toast.success("Lender deleted");
    nav("/lenders");
  };

  const addExampleDeal = async (e) => {
    e.preventDefault();
    const payload = { ...dealForm };
    ["term_months", "buy_rate", "sell_rate"].forEach((k) => {
      if (payload[k] === "" || payload[k] == null) delete payload[k];
      else payload[k] = Number(payload[k]);
    });
    await api.post(`/lenders/${id}/example-deals`, payload);
    setShowDealForm(false); setDealForm({});
    toast.success("Example deal added");
    reload();
  };

  if (!lender) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/lenders" className="hover:text-slate-800 inline-flex items-center gap-1"><ArrowLeft size={14} /> Lender directory</Link>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TierBadge tier={lender.tier} tiers={lender.tiers} />
            <StatusBadge status={lender.status} />
            {lender.is_hot && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded border bg-orange-50 text-orange-700 border-orange-200">🔥 Hot</span>}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 inline-flex items-baseline gap-3">
            {lender.is_hot && <span className="text-3xl">🔥</span>}
            {lender.name}
          </h1>
          <div className="text-slate-600 mt-1">{[lender.contact_name, lender.contact_email, lender.contact_phone].filter(Boolean).join(" · ") || <span className="text-slate-400 italic text-sm">No contact info yet — add via edit</span>}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild variant="outline" data-testid={TID.lenderEditBtn}>
            <Link to={`/lenders/${id}/edit`}><PencilSimple size={14} className="mr-1.5" /> Propose edit</Link>
          </Button>
          {canManage && (
            <>
              <Button
                onClick={async () => {
                  await api.post(`/lenders/${id}/hot`, null, { params: { on: !lender.is_hot } });
                  toast.success(!lender.is_hot ? "Marked as 🔥 Hot" : "Removed hot flag");
                  reload();
                }}
                variant="outline"
                className={lender.is_hot ? "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100" : ""}
                data-testid="lender-toggle-hot"
              >
                <span className="mr-1.5">🔥</span>{lender.is_hot ? "Remove hot" : "Mark hot"}
              </Button>
              <Button onClick={handleDelete} variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200" data-testid={TID.lenderDeleteBtn}>
                <Trash size={14} className="mr-1.5" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notes">Notes <span className="ml-1 text-[10px] text-slate-500">({notes.length})</span></TabsTrigger>
          <TabsTrigger value="deals">Example deals <span className="ml-1 text-[10px] text-slate-500">({deals.length})</span></TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Section title="Funding criteria">
              <Field label="Product types" value={(lender.product_types || []).join(", ")} />
              <Field label="Funding range" value={moneyR(lender.min_funding, lender.max_funding)} mono />
              <Field label="Preferred funding" value={lender.preferred_funding != null ? `$${Number(lender.preferred_funding).toLocaleString()}` : null} mono />
              <Field label="Term range (mo)" value={`${lender.min_term_months ?? "—"} – ${lender.max_term_months ?? "—"}`} mono />
              <Field label="Speed to fund" value={lender.speed_to_fund} />
              <Field label="Required docs" value={(lender.required_documents || []).join(", ")} />
              <Field label="Renewal policy" value={lender.renewal_policy} />
              <Field label="Stacking policy" value={lender.stacking_policy} />
            </Section>
            <Section title="Underwriting thresholds">
              <Field label="Min monthly revenue" value={lender.min_monthly_revenue != null ? `$${Number(lender.min_monthly_revenue).toLocaleString()}` : null} mono />
              <Field label="Min avg daily balance" value={lender.min_avg_daily_balance != null ? `$${Number(lender.min_avg_daily_balance).toLocaleString()}` : null} mono />
              <Field label="Min time in business" value={lender.min_time_in_business_months != null ? `${lender.min_time_in_business_months} months` : null} />
              <Field label="Min credit score" value={lender.min_credit_score} mono />
              <Field label="Max negative days" value={lender.max_negative_days} mono />
              <Field label="Max NSFs" value={lender.max_nsfs} mono />
            </Section>
            <Section title="Industries">
              <Field label="Funded" value={(lender.industries_funded || []).join(", ")} />
              <Field label="Preferred" value={(lender.preferred_industries || []).join(", ")} />
              <Field label="Restricted" value={(lender.restricted_industries || []).join(", ")} />
            </Section>
            <Section title="Geography & pricing">
              <Field label="States funded" value={(lender.states_funded || []).join(", ")} />
              <Field label="States NOT funded" value={(lender.states_not_funded || []).join(", ")} />
              <Field label="Commission structure" value={lender.commission_structure} />
              <Field label="Commission points" value={lender.commission_points} />
              <Field label="Fees" value={lender.fees} />
              <Field label="Buy/Sell rate notes" value={lender.buy_sell_rate_notes} />
            </Section>
            <Section title="Profile preferences" cols={2}>
              <Field label="Preferred deal profile" value={lender.preferred_deal_profile} />
              <Field label="Red flags" value={lender.red_flags} />
              <Field label="Notes" value={lender.notes} />
            </Section>
            <Section title="Provenance" cols={2}>
              <Field label="Updated by" value={lender.updated_by} />
              <Field label="Last updated" value={lender.updated_at ? new Date(lender.updated_at).toLocaleString() : null} />
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4">
          <form onSubmit={addNote} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
            <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">New note</label>
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="e.g. Funded a $42k restaurant deal yesterday, prefers >12 mo TIB…" data-testid={TID.noteText} />
            <div className="flex justify-end">
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.noteSubmit}>
                <Plus size={14} className="mr-1.5" />Add note
              </Button>
            </div>
          </form>
          <div className="space-y-3">
            {notes.length === 0 && <div className="text-slate-500 text-sm">No notes yet.</div>}
            {notes.map((n) => (
              <div key={n.id} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <Note size={12} weight="duotone" />
                  <span className="font-semibold text-slate-700">{n.author_name || n.author_email}</span>
                  <span>·</span>
                  <span>{new Date(n.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="deals" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowDealForm((s) => !s)} className="bg-slate-900 hover:bg-slate-800 text-white">
              <Plus size={14} className="mr-1.5" />Log example funded deal
            </Button>
          </div>

          {showDealForm && (
            <form onSubmit={addExampleDeal} className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
              {[
                ["business_industry", "Industry", "text", "e.g. Restaurant"],
                ["term_months", "Term (months)", "number", "12"],
                ["buy_rate", "Buy rate", "number", "1.28"],
                ["sell_rate", "Sell rate", "number", "1.39"],
              ].map(([k, label, type, ph]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</label>
                  <input
                    type={type}
                    step={type === "number" ? "any" : undefined}
                    placeholder={ph}
                    value={dealForm[k] ?? ""}
                    onChange={(e) => setDealForm({ ...dealForm, [k]: e.target.value })}
                    className="mt-1 w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:border-slate-900 focus:ring-2 focus:ring-slate-200 outline-none"
                  />
                </div>
              ))}
              <div className="md:col-span-4">
                <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Notes (why approved, optional)</label>
                <Textarea rows={2} value={dealForm.notes || ""} onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })} />
              </div>
              <div className="md:col-span-4 flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => { setShowDealForm(false); setDealForm({}); }}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Save deal</Button>
              </div>
            </form>
          )}

          {deals.length === 0 ? (
            <div className="text-slate-500 text-sm">No example deals logged yet.</div>
          ) : (
            <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Industry</th>
                    <th className="text-left px-4 py-3 font-semibold">Term</th>
                    <th className="text-left px-4 py-3 font-semibold">Buy rate</th>
                    <th className="text-left px-4 py-3 font-semibold">Sell rate</th>
                    <th className="text-left px-4 py-3 font-semibold">Spread</th>
                    <th className="text-left px-4 py-3 font-semibold">Notes</th>
                    <th className="text-left px-4 py-3 font-semibold">Logged</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((d) => {
                    const spread = d.buy_rate != null && d.sell_rate != null ? (Number(d.sell_rate) - Number(d.buy_rate)).toFixed(3) : null;
                    return (
                      <tr key={d.id} className="border-t border-slate-200">
                        <td className="px-4 py-3"><Briefcase size={14} className="inline mr-1 text-slate-400" />{d.business_industry || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{d.term_months != null ? `${d.term_months} mo` : "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{d.buy_rate != null ? Number(d.buy_rate).toFixed(3) : "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{d.sell_rate != null ? Number(d.sell_rate).toFixed(3) : "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-emerald-700">{spread ?? "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-600 max-w-md truncate">{d.notes || "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(d.created_at).toLocaleDateString()}<div className="text-[10px] text-slate-400">{d.author_email}</div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Section = ({ title, children, cols }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-6 ${cols === 2 ? "lg:col-span-2" : ""}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">{title}</div>
    <div>{children}</div>
  </div>
);
